import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';

/**
 * SaleWorkflowExecutions - List all Sale Order workflow executions
 * Separate component for Sales Order debugging
 */
const SaleWorkflowExecutions = ({ onShowApiLogs }) => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0
  });

  useEffect(() => {
    fetchExecutions();
  }, [pagination.offset]);

  const fetchExecutions = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('sankalvax_token');

      const response = await fetch(
        `/api/sale/workflows/debug/executions?limit=${pagination.limit}&offset=${pagination.offset}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch executions: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setExecutions(data.executions || []);
        setPagination(prev => ({
          ...prev,
          total: data.total || 0
        }));
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to fetch executions');
      }
    } catch (err) {
      console.error('Error fetching executions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return 'N/A';
    const start = new Date(startedAt);
    const end = new Date(completedAt);
    const durationMs = end - start;
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      completed: 'bg-green-100 text-green-800 border-green-200',
      running: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      awaiting_user: 'bg-blue-100 text-blue-800 border-blue-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return statusStyles[status] || statusStyles.cancelled;
  };

  const handlePrevPage = () => {
    setPagination(prev => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit)
    }));
  };

  const handleNextPage = () => {
    setPagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit
    }));
  };

  if (loading && executions.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6 animate-spin text-orange-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <span className="text-gray-600">Loading Sale Order executions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          Showing {executions.length} of {pagination.total} Sale Order executions
        </div>
        <button
          onClick={fetchExecutions}
          className="px-4 py-2 text-sm bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="text-red-600 font-medium">Error:</div>
          <div className="text-sm text-red-500">{error}</div>
        </div>
      )}

      {/* Executions Table */}
      {executions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <div className="text-gray-500 text-lg">No Sale Order executions found</div>
          <div className="text-gray-400 text-sm mt-1">Start a Sales Order workflow to see executions here</div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <table className="w-full">
            <thead className="bg-orange-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                  Run ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                  Workflow
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                  Current Step
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-orange-800 uppercase tracking-wider">
                  Steps
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-orange-800 uppercase tracking-wider">
                  API Calls
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-orange-800 uppercase tracking-wider">
                  Tokens
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-orange-800 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                  Started
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-orange-800 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-orange-800 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {executions.map((execution) => (
                <tr key={execution.run_id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                      {execution.run_id.substring(0, 8)}...
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {execution.workflow_name || `Workflow ${execution.workflow_id}`}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadge(execution.status)}`}>
                      {execution.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-gray-600">
                      {execution.current_step || 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-medium text-gray-900">{execution.step_count}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-medium text-blue-600">{execution.api_call_count}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-mono text-gray-600">
                      {execution.total_tokens?.toLocaleString() || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-mono text-green-600">
                      ${execution.total_cost?.toFixed(4) || '0.0000'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {formatTimestamp(execution.started_at)}
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-500">
                    {formatDuration(execution.started_at, execution.completed_at)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => onShowApiLogs(execution.run_id)}
                      className="px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.total > pagination.limit && (
            <div className="px-4 py-3 border-t bg-gray-50 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Page {Math.floor(pagination.offset / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handlePrevPage}
                  disabled={pagination.offset === 0}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SaleWorkflowExecutions;
